import Browser from 'bowser';

import { AuthLocalStorageKeys } from 'common/auth/constants';
import { userMustAcceptPrivacyPolicy } from 'common/auth/privacyPolicyAcceptance';
import { getCurrentLng } from 'common/i18n/i18n';
import { hideOverriddenPendoTrainings } from 'common/utils/hideOverriddenPendoTrainings';

import type { ActiveStudySite, AuthUser } from '../auth/types';

import type { Analytics } from './analytics.provider';
import { fetchStudyPendoMetadata } from './utils';

export default class PendoAnalytics implements Analytics {
    /** Last-write-wins guard: identifyUser is fire-and-forget, so a slow lookup must not overwrite a newer one. */
    private latestIdentifyRequest = 0;

    public initialize(user?: AuthUser): void {
        window?.pendo?.initialize?.({
            ...(!!user && {
                events: {
                    guidesLoaded: () => hideOverriddenPendoTrainings(user),
                },
            }),
        });
    }
    public async identifyUser(user: AuthUser): Promise<void> {
        // parse is not exported from bowser as a separate function. It is exported as Browser.parse
        // eslint-disable-next-line import/no-named-as-default-member
        const { os, platform } = Browser.parse(window.navigator.userAgent);
        const { pk, email, username, subType, userStudies, roleBClaim, roleVClaim, createdAt, type } = user;
        const activeStudySite: ActiveStudySite = JSON.parse(
            localStorage.getItem(AuthLocalStorageKeys.activeStudySite) as string
        );

        if (userMustAcceptPrivacyPolicy(user)) {
            return;
        }

        const pendo = window?.pendo;

        // on guides loaded hide any trainings that have been overridden by training override in admin
        pendo?.events?.guidesLoaded?.(function () {
            hideOverriddenPendoTrainings(user);
        });

        const studyId = activeStudySite?.activeStudy?.studyId || userStudies?.[0]?.studyId;
        const requestId = ++this.latestIdentifyRequest;
        // Customer and sponsor both come from the study being reported, not from the user.
        const { customerId, customerName, sponsorId, sponsorName } = await fetchStudyPendoMetadata(studyId);

        // Switching study re-invokes identifyUser without awaiting the previous call, so a lookup that
        // resolves out of order would report the old study — and now the old account.id too.
        if (requestId !== this.latestIdentifyRequest) {
            return;
        }

        pendo?.identify?.({
            visitor: {
                id: pk,
                email,
                userCreation: createdAt,
                username,
                userType: type,
                subType,
                language: getCurrentLng(),
                preferredLanguage: getCurrentLng(),
                studyId,
                siteId: activeStudySite?.activeSite?.siteId || userStudies?.[0]?.userStudySites?.[0]?.siteId,
                studyName: activeStudySite?.activeStudy?.studyName || userStudies?.[0]?.studyName,
                siteName: activeStudySite?.activeSite?.siteName || userStudies?.[0]?.userStudySites?.[0]?.siteName,
                customerId,
                customerName,
                sponsorId,
                sponsorName,
                baseRole: roleBClaim?.data,
                roleVariant: roleVClaim?.name,
                device: platform.type === 'desktop' ? 'Web' : platform.type,
                operatingSystem: os.name,
                osVersion: os.version,
                accountName: user.subscriberDetails?.name,
            },
            account: {
                // Keyed on the customer, not the subscriber; the subscriber is the no-customer floor.
                id: customerId ?? user.subscriberPk,
                name: user.subscriberDetails?.name,
                // planLevel:    // Optional
                // planPrice:    // Optional
                // creationDate: // Optional

                // You can add any additional account level key-values here,
                // as long as it's not one of the above reserved names.
            },
        });
    }
    public anonymousUser(pendoObj: string): void {
        // parse is not exported from bowser as a separate function. It is exported as Browser.parse
        // eslint-disable-next-line import/no-named-as-default-member
        const { os, platform } = Browser.parse(window.navigator.userAgent);
        (window as any).pendo[pendoObj]({
            visitor: {
                language: getCurrentLng(),
                preferredLanguage: getCurrentLng(),
                device: platform.type === 'desktop' ? 'Web' : platform.type,
                operatingSystem: os.name,
                osVersion: os.version,
            },

            account: {
                //
            },
        });
    }

    public clearSession() {
        (window as any).pendo.clearSession();
    }

    public track(): void {}
}
